import React from 'react';
import { useLibraryBlock } from './blockLibraryContext';
import { resolveFormBuilderBlockOptions } from './optionBlocks';
import type { CanvasBlock, SmartFieldDef } from '../types';
import { resolveFormBuilderBlockIcon } from './blockIcons';
import { cn } from '../../lib/cn';
import { Icon } from '../../components/Icon/Icon';
import { FormBuilderBlockIcon } from '../../components/Forms/FormBuilderBlockIcons';

function FieldLabel({
  label,
  required,
  locked,
  badge,
  hint,
}: {
  label: string;
  required?: boolean;
  locked?: boolean;
  badge?: { label: string; color: string };
  hint?: string;
}) {
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
      <span className="text-[12.5px] font-medium text-[var(--ink-2)]">
        {label}
        {required ? <span className="text-[var(--terracotta)]"> *</span> : null}
      </span>
      {locked ? <Icon kind="lock" size={10} className="text-[var(--ink-4)]" /> : null}
      {badge ? (
        <span
          className="rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
          style={{
            background: `color-mix(in srgb, ${badge.color} 12%, var(--cream-1))`,
            color: badge.color,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {badge.label}
        </span>
      ) : null}
      {hint ? (
        <span className="ml-auto font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">{hint}</span>
      ) : null}
    </div>
  );
}

function BasicPreview({
  type,
  placeholder,
  options,
}: {
  type: string;
  placeholder?: string;
  options?: string[];
}) {
  const base = 'fm-field-preview';
  const dropdownOptions = options?.filter(Boolean) ?? [];
  const choiceOptions =
    dropdownOptions.length > 0 ? dropdownOptions : type === 'radio' ? ['Option A', 'Option B'] : ['Option 1', 'Option 2', 'Option 3'];
  switch (type) {
    case 'heading':
      return null;
    case 'paragraph':
      return null;
    case 'section_break':
      return <div className="my-2 h-px bg-[var(--cream-2)]" />;
    case 'patient_email':
      return <div className={base}>name@example.com</div>;
    case 'patient_mobile_phone':
    case 'patient_home_phone':
    case 'patient_work_phone':
      return <div className={base}>(555) 123-4567</div>;
    case 'patient_dob':
    case 'date_picker':
      return (
        <div className={cn(base, 'flex items-center justify-between')}>
          MM / DD / YYYY
          <Icon kind="calendar" size={14} className="text-[var(--ink-4)]" />
        </div>
      );
    case 'patient_ssn_last4':
      return <div className={cn(base, 'font-[var(--font-mono)]')}>•••• (encrypted)</div>;
    case 'textarea':
      return <div className={cn(base, 'min-h-[56px]')}>{placeholder || 'Multi-line text…'}</div>;
    case 'number_input':
    case 'patient_family_size':
      return <div className={base}>0</div>;
    case 'patient_income':
      return (
        <div className={cn(base, 'relative pl-6')}>
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]">$</span>
          0.00
        </div>
      );
    case 'dropdown':
    case 'patient_title':
    case 'patient_suffix':
    case 'patient_gender':
    case 'patient_language':
    case 'patient_marital_status':
      return (
        <div className={cn(base, 'flex items-center justify-between')}>
          Select…
          <Icon kind="down" size={12} className="text-[var(--ink-3)]" />
        </div>
      );
    case 'radio':
    case 'patient_contact_method':
      return (
        <div className="flex flex-wrap gap-3.5 pt-1">
          {choiceOptions.map((o) => (
            <label key={o} className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ink-1)]">
              <span className="size-4 shrink-0 rounded-full border-[1.5px] border-[var(--cream-shadow)]" />
              {o}
            </label>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <label className="inline-flex items-center gap-2 text-[13px] text-[var(--ink-1)]">
          <span className="size-4 shrink-0 rounded border-[1.5px] border-[var(--cream-shadow)]" />
          {choiceOptions[0] ?? placeholder ?? 'Check this box'}
        </label>
      );
    case 'checkbox_group':
      return (
        <div className="flex flex-col gap-1.5 pt-1">
          {choiceOptions.map((o) => (
            <label key={o} className="inline-flex items-center gap-2 text-[13px] text-[var(--ink-1)]">
              <span className="size-4 shrink-0 rounded border-[1.5px] border-[var(--cream-shadow)]" />
              {o}
            </label>
          ))}
        </div>
      );
    case 'patient_allergies':
    case 'patient_conditions':
    case 'patient_medications':
      return (
        <div className={cn(base, 'flex flex-wrap gap-1')}>
          {['Penicillin', 'Latex'].map((t) => (
            <span key={t} className="rounded-full bg-[var(--cream-0)] px-2 py-0.5 text-[11.5px] text-[var(--ink-2)]">
              {t} ×
            </span>
          ))}
          <span className="text-[var(--ink-4)]">Search to add…</span>
        </div>
      );
    case 'patient_address':
      return <div className={base}>123 Main St, Springfield…</div>;
    case 'file_upload':
      return (
        <div className={cn(base, 'border-dashed py-4 text-center')}>
          <Icon kind="upload" size={14} className="mx-auto mb-1.5 opacity-60" />
          <div>Drag a file or click to upload</div>
        </div>
      );
    case 'signature_pad':
      return (
        <div className={cn(base, 'flex h-[100px] items-center justify-center border-dashed bg-[var(--cream-0)] italic')}>
          Sign here
        </div>
      );
    case 'rich_text_display':
      return (
        <div className="rounded-[7px] bg-[var(--cream-0)] px-3 py-2.5 text-[12.5px] italic leading-relaxed text-[var(--ink-3)]">
          Read-only formatted text. Customize via the AI prompt in the right panel.
        </div>
      );
    default:
      return <div className={base}>{placeholder || 'Placeholder…'}</div>;
  }
}

function SmartFieldInput({ field }: { field: SmartFieldDef }) {
  const prefill = field.prefill;
  const base = 'fm-field-preview';

  switch (field.type) {
    case 'rich_text_locked':
      return (
        <div className="rounded-[9px] border border-[color-mix(in_srgb,var(--gold)_25%,var(--cream-2))] bg-[var(--gold-tint)] px-3 py-2.5">
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--gold)]">
            <Icon kind="lock" size={9} />
            {field.label} · Locked legal text
          </div>
          <div className="whitespace-pre-wrap text-[12px] leading-relaxed text-[var(--ink-1)]">
            {field.content || 'Managed by ELVA legal team. Cannot be edited.'}
          </div>
        </div>
      );
    case 'rich_text':
      return (
        <div>
          <FieldLabel
            label={field.label}
            {...(field.required !== undefined ? { required: field.required } : {})}
            {...(field.locked !== undefined ? { locked: field.locked } : {})}
            {...(field.hint ? { hint: field.hint } : {})}
          />
          <div
            className={cn(
              'rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3 py-2.5 text-[12px] leading-relaxed',
              field.content ? 'text-[var(--ink-1)]' : 'italic text-[var(--ink-3)]',
            )}
          >
            {field.content || 'Rich text content — editable via AI prompt in the right panel.'}
          </div>
        </div>
      );
    case 'signature':
      return (
        <div className="relative flex h-24 items-center justify-center rounded-[9px] border-[1.5px] border-dashed border-[var(--cream-shadow)] bg-[var(--cream-0)]">
          <div className="absolute bottom-3 left-3.5 right-3.5 border-b border-dashed border-[var(--ink-4)]" />
          <span className="inline-flex items-center gap-1.5 text-[12.5px] italic text-[var(--ink-4)]">
            <Icon kind="pencil" size={12} />
            Sign with your finger or stylus
          </span>
        </div>
      );
    case 'file':
      return (
        <div className="rounded-[9px] border-[1.5px] border-dashed border-[var(--cream-shadow)] bg-[var(--cream-0)] px-3 py-4 text-center text-[12.5px] text-[var(--ink-3)]">
          <Icon kind="upload" size={14} className="mx-auto mb-1.5 opacity-70" />
          <div>Drag a file or tap to upload</div>
          <div className="mt-1 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">Camera capture available</div>
        </div>
      );
    case 'dynamic_list':
      return (
        <div className="rounded-[9px] border border-dashed border-[var(--cream-shadow)] bg-[var(--cream-0)] p-3">
          <p className="mb-2 text-[11.5px] leading-snug text-[var(--ink-3)]">
            For each additional person present during recordings, add their name and signature.
          </p>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-[7px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[12px] font-medium text-[var(--ink-2)]"
          >
            <Icon kind="plus" size={12} />
            Add person present
          </button>
        </div>
      );
    case 'checkbox':
      return (
        <label className="inline-flex items-start gap-2 text-[13px] leading-snug text-[var(--ink-1)]">
          <span className="mt-0.5 size-4 shrink-0 rounded border-[1.5px] border-[var(--cream-shadow)] bg-[var(--cream-1)]" />
          <span>{field.label}</span>
        </label>
      );
    case 'checkbox_group':
      return (
        <div className="flex flex-col gap-1.5 pt-1">
          {(field.options ?? ['First', 'Second', 'Third']).map((opt) => (
            <label key={opt} className="inline-flex items-center gap-2 text-[13px] text-[var(--ink-1)]">
              <span className="size-4 shrink-0 rounded border-[1.5px] border-[var(--cream-shadow)]" />
              {opt}
            </label>
          ))}
        </div>
      );
    case 'radio':
      return (
        <div className="flex flex-wrap gap-3.5 pt-1">
          {(field.options ?? ['Option A', 'Option B']).map((opt) => (
            <label key={opt} className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ink-1)]">
              <span className="size-4 shrink-0 rounded-full border-[1.5px] border-[var(--cream-shadow)]" />
              {opt}
            </label>
          ))}
        </div>
      );
    case 'date':
      return (
        <div className={cn(base, 'flex items-center justify-between')}>
          <span>{prefill === 'Today' ? new Date().toLocaleDateString() : (prefill as string) || 'MM / DD / YYYY'}</span>
          <Icon kind="calendar" size={14} className="text-[var(--ink-4)]" />
        </div>
      );
    case 'dropdown':
      return (
        <div className={cn(base, 'flex items-center justify-between')}>
          <span className={prefill ? 'text-[var(--ink-0)]' : 'text-[var(--ink-3)]'}>{prefill || 'Select…'}</span>
          <Icon kind="down" size={12} className="text-[var(--ink-3)]" />
        </div>
      );
    case 'currency':
      return (
        <div className={cn(base, 'relative pl-6')}>
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]">$</span>
          {(prefill as string) || '0.00'}
        </div>
      );
    case 'textarea':
      return <div className={cn(base, 'min-h-[60px]')}>{(prefill as string) || 'Multi-line text…'}</div>;
    case 'search':
      return (
        <div className={cn(base, 'relative pl-8')}>
          <Icon kind="search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)]" />
          {(prefill as string) || field.hint || 'Search…'}
        </div>
      );
    case 'google':
      return (
        <div className={cn(base, 'relative pl-9 italic text-[var(--ink-3)]')}>
          <Icon kind="pin" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--info)]" />
          {field.hint || 'Start typing your address…'}
        </div>
      );
    case 'search_multi':
      return (
        <div className={cn(base, 'flex min-h-[38px] flex-wrap items-center gap-1')}>
          {Array.isArray(prefill) &&
            prefill.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-full bg-[var(--ai-tint)] px-2 py-0.5 text-[11.5px] font-medium text-[var(--ai-3)]"
              >
                {tag}
                <Icon kind="x" size={10} className="opacity-60" />
              </span>
            ))}
          <span className="ml-1 text-[12.5px] italic text-[var(--ink-4)]">{field.hint || 'Search and add…'}</span>
        </div>
      );
    default:
      return <div className={base}>{(prefill as string) || 'Placeholder…'}</div>;
  }
}

function SmartFieldRender({ field, override }: { field: SmartFieldDef; override?: { visible?: boolean; required?: boolean } }) {
  const required = override?.required ?? field.required;
  if (field.type === 'rich_text_locked' || field.type === 'checkbox') {
    return <SmartFieldInput field={field} />;
  }
  return (
    <div>
      <FieldLabel
        label={field.label}
        {...(required !== undefined ? { required } : {})}
        {...(field.locked !== undefined ? { locked: field.locked } : {})}
        {...(field.hint ? { hint: field.hint } : {})}
      />
      <SmartFieldInput field={field} />
    </div>
  );
}

export function BasicBlockRender({ block }: { block: CanvasBlock }) {
  const def = useLibraryBlock(block.typeId);
  const cfg = block.config ?? {};
  const previewOptions = resolveFormBuilderBlockOptions(block.typeId, cfg.options);
  switch (block.typeId) {
    case 'heading':
      return (
        <div className="text-[22px] font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
          {cfg.label || 'Heading'}
        </div>
      );
    case 'paragraph':
      return (
        <div className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-[var(--ink-1)]">
          {cfg.label?.trim() ? cfg.label : 'Paragraph text'}
        </div>
      );
    case 'section_break':
      return <div className="my-2 h-px bg-[var(--cream-2)]" />;
    case 'rich_text_display':
      // Mirrors the patient view: the block's text *is* the content, not a label.
      return (
        <div className="whitespace-pre-wrap rounded-[var(--r-md)] bg-[var(--cream-0)] px-3 py-2.5 text-[12.5px] leading-relaxed text-[var(--ink-1)]">
          {cfg.label?.trim() ? cfg.label : 'Read-only formatted text.'}
        </div>
      );
    default:
      return (
        <div>
          <FieldLabel
            label={cfg.label || def?.label || 'Field'}
            {...(cfg.required !== undefined ? { required: cfg.required } : {})}
            {...(cfg.helpText ? { hint: cfg.helpText } : {})}
          />
          <BasicPreview
            type={block.typeId}
            {...(cfg.placeholder ? { placeholder: cfg.placeholder } : {})}
            {...(previewOptions ? { options: previewOptions } : {})}
          />
        </div>
      );
  }
}

export function PatientBlockRender({ block }: { block: CanvasBlock }) {
  const def = useLibraryBlock(block.typeId);
  return (
    <div>
      <FieldLabel
        label={block.customLabel || def?.label || 'Patient field'}
        required={block.required !== false}
        badge={{ label: 'PMS', color: 'var(--info)' }}
        {...(def?.desc ? { hint: def.desc } : {})}
      />
      <BasicPreview type={block.typeId} />
    </div>
  );
}

export function SmartBlockRender({ block }: { block: CanvasBlock }) {
  const tpl = useLibraryBlock(block.typeId);
  // Degradation rule (D41): a block type the server no longer serves must still
  // draw — never a blank. Render a generic card from what the canvas block knows.
  if (!tpl) return <UnknownBlockRender block={block} />;
  const overrides = block.overrides ?? {};
  const visibleFields = (tpl.fields ?? []).filter((f) => overrides[f.id]?.visible !== false);
  const accent = tpl.accentColor ?? 'var(--ai-3)';

  return (
    <div>
      <header className="mb-3.5 flex items-center gap-2.5 border-b border-[var(--cream-2)] pb-3">
        <div
          className="flex size-8 shrink-0 items-center justify-center rounded-lg"
          style={{
            backgroundColor: `color-mix(in srgb, ${accent} 12%, var(--cream-1))`,
            color: accent,
          }}
        >
          <FormBuilderBlockIcon icon={resolveFormBuilderBlockIcon(tpl)} size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13.5px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">{tpl.label}</span>
            {tpl.compliance ? (
              <span className="fm-badge-compliance">
                <Icon kind="shield" size={8} />
                Compliance
              </span>
            ) : (
              <span className="fm-badge-smart">
                <Icon kind="sparkle" size={8} />
                Smart
              </span>
            )}
            {block.condition ? (
              <span className="inline-flex items-center gap-1 rounded-[var(--r-pill)] border border-[color-mix(in_srgb,var(--gold)_25%,var(--cream-2))] bg-[var(--gold-tint)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--gold)]">
                <Icon kind="bolt" size={9} />
                Conditional
              </span>
            ) : null}
          </div>
          {tpl.desc ? <p className="mt-0.5 text-[11.5px] text-[var(--ink-3)]">{tpl.desc}</p> : null}
        </div>
      </header>

      {block.customIntro ? (
        <div className="mb-3.5 rounded-lg border-l-[3px] border-[var(--gold)] bg-[var(--cream-0)] px-3 py-2.5 text-[12.5px] leading-relaxed text-[var(--ink-1)]">
          {block.customIntro}
        </div>
      ) : null}

      <div className="flex flex-col gap-3.5">
        {visibleFields.map((field) => (
          <SmartFieldRender
            key={field.id}
            field={field}
            {...(overrides[field.id] ? { override: overrides[field.id] } : {})}
          />
        ))}
      </div>

      {block.customOutro ? (
        <div className="mt-3.5 rounded-lg border-l-[3px] border-[var(--gold)] bg-[var(--cream-0)] px-3 py-2.5 text-[12.5px] leading-relaxed text-[var(--ink-1)]">
          {block.customOutro}
        </div>
      ) : null}

      {(tpl.fields?.length ?? 0) > visibleFields.length ? (
        <p className="mt-3 flex items-center gap-1.5 border-t border-dashed border-[var(--cream-2)] pt-2.5 font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">
          <span className="opacity-70">○</span>
          {tpl.fields!.length - visibleFields.length} field
          {tpl.fields!.length - visibleFields.length !== 1 ? 's' : ''} hidden — toggle in the right panel
        </p>
      ) : null}
    </div>
  );
}

function UnknownBlockRender({ block }: { block: CanvasBlock }) {
  return (
    <div className="rounded-[9px] border border-dashed border-[var(--cream-shadow)] bg-[var(--cream-0)] px-3.5 py-3">
      <div className="flex items-center gap-2">
        <Icon kind="sparkle" size={12} className="shrink-0 text-[var(--ink-4)]" />
        <span className="text-[13px] font-semibold text-[var(--ink-1)]">{block.title || block.typeId}</span>
      </div>
      <p className="mt-1 font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">
        This block isn’t in the current library — it will render from the server definition when published.
      </p>
    </div>
  );
}

export function CanvasBlockContent({ block }: { block: CanvasBlock }) {
  if (block.source === 'basic') return <BasicBlockRender block={block} />;
  if (block.source === 'patient') return <PatientBlockRender block={block} />;
  return <SmartBlockRender block={block} />;
}
